import { useState } from 'react';

import { Label, Radio, dialogService, toastService } from '@thefarmersfront/kitchen';

import { ExcelUploadModal } from '~/components/common';

import { usePostOrdersUpload } from '~/hooks/delivery-agency/api';

import { DELIVERY_AGENCY, FILE_SIZE_LIMIT, TOAST_MESSAGE } from '~/constants';

import type { TypeDeliveryAgencyOrdersUploadResult, TypePostOrdersUploadParams } from '~/types/delivery-agency.types';

const infoTexts = [
  '엑셀 파일 최대 용량은 10MB입니다. 초과할 시 등록에 실패할 수 있습니다.',
  '최대 10초 정도 소요되니 완료 전까지 브라우저를 종료하지 말아주세요.',
  '1개의 주문을 2개 이상의 엑셀 파일에 나눠서 등록하지 말아주세요.',
];

type OutboundGroupType = TypePostOrdersUploadParams['outboundGroupType'];

const OUTBOUND_GROUP_INFO: { label: string; value: OutboundGroupType }[] = [
  { label: '합포장', value: 'BY_RECEIVER' },
  { label: '개별 포장', value: 'NO_GROUPING' },
];

const DeliveryAgencyOrderExcelUploadModal = ({
  open,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  onCancel: () => void;
  onConfirm: (data: TypeDeliveryAgencyOrdersUploadResult) => void;
}) => {
  const { requestOrdersUpload } = usePostOrdersUpload();

  const [outboundGroupType, setOutboundGroupType] = useState<OutboundGroupType>();

  const handleConfirm = (file: File) => {
    if (FILE_SIZE_LIMIT < file.size) {
      dialogService.addDialog({
        children: '10MB 이하 파일만 첨부할 수 있습니다.',
      });
      return;
    }

    if (!outboundGroupType) {
      dialogService.addDialog({
        children: '합포장 여부를 선택해 주세요.',
      });
      return;
    }

    requestOrdersUpload(
      { file, outboundGroupType },
      {
        onSuccess: (data) => {
          onConfirm(data);
        },
        onError: (error) => {
          if (error?.code === DELIVERY_AGENCY.BACKEND_ERROR_DIALOG_CODE) {
            dialogService.addDialog({
              children: error?.message,
            });
            return;
          }
          toastService.addToast({ type: 'error', message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR });
        },
      }
    );
  };

  const handleOutboundGroupTypeChange = (type: OutboundGroupType) => {
    setOutboundGroupType((prevType) => (prevType === type ? undefined : type));
  };

  return (
    <ExcelUploadModal
      title="주문 엑셀 일괄 등록"
      infoTexts={infoTexts}
      open={open}
      onCancel={onCancel}
      onConfirm={handleConfirm}
      extraContent={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10, marginBottom: 24 }}>
          <Label required title="합포장 여부" />
          <div style={{ display: 'flex', gap: 12 }}>
            {OUTBOUND_GROUP_INFO.map((option) => (
              <Radio
                key={option.value}
                checked={outboundGroupType === option.value}
                onChange={() => handleOutboundGroupTypeChange(option.value)}
                label={option.label}
                size="medium"
              />
            ))}
          </div>
        </div>
      }
    />
  );
};

export default DeliveryAgencyOrderExcelUploadModal;
